/**
 * DrillPanel — conteudo do SlideOverPanel ao clicar uma linha do
 * acompanhamento. Mostra:
 *   - Resumo: cliente, etapa atual, dias, prazo
 *   - Timeline horizontal das 7 etapas (atual destacada)
 *   - Atalho "Ver ficha completa" (rota apropriada por origem)
 *
 * Pure component; recebe `item` consolidado + callback `onNavigate(rota)`.
 */
import React from "react";
import { FiExternalLink } from "react-icons/fi";

import { naOrdemDaLinha } from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import { fmtData } from "../Orcamentos/helpers";

import {
  ETAPA_LABEL,
  ETAPA_TONE,
  ORDEM_ETAPAS,
  PRIORIDADE_TONE,
} from "./classificador";
import { atrasoTone, fmtAtraso, fmtDias } from "./helpers";
import {
  Block,
  FieldLabel,
  FieldRow,
  FieldValue,
  InlineHint,
  OriginBadge,
  Pill,
  PrimaryButton,
  SectionTitle,
  Timeline,
  TimelinePasso,
} from "./styles";

/**
 * Timeline horizontal simples — sao 7 dots, etapa atual destacada.
 * Etapas anteriores ficam check; futuras ficam neutro.
 */
const MARCOS_DO_EXECUTIVO = {
  ehRevisao: (marco) => marco.label === "Revisão",
  ehAprovacao: (marco) => marco.label === "Aprovação do executivo",
};

function TimelineHorizontal({ etapaAtual }) {
  const idxAtual = ORDEM_ETAPAS.indexOf(etapaAtual);
  return (
    <Timeline>
      {ORDEM_ETAPAS.map((etapa, idx) => {
        const isAtual = idx === idxAtual;
        const isPassada = idx < idxAtual;
        const tone = isAtual ? ETAPA_TONE[etapa] : isPassada ? "success" : "neutral";
        return (
          <TimelinePasso key={etapa} $atual={isAtual} title={ETAPA_LABEL[etapa]}>
            <Pill $tone={tone} className="marca">
              {isPassada ? "✓" : isAtual ? "•" : "○"}
            </Pill>
            <span className="nome">{ETAPA_LABEL[etapa]}</span>
          </TimelinePasso>
        );
      })}
    </Timeline>
  );
}

/**
 * Datas-chave de marcos coletadas da planilha (quando origem=planilha).
 * Backend nao tem essas datas hoje — mostra so created_at/iniciado_*.
 */
function DatasMarcos({ item }) {
  if (item.origem === "planilha") {
    const amb = item.raw;
    const marcos = [
      { label: "Liberação da obra", iso: amb?.[F.LIBERACAO_DA_OBRA] },
      { label: "Medição realizada", iso: amb?.[F.MEDICAO_REALIZADO] },
      { label: "Executivo realizado", iso: amb?.[F.EXECUTIVO_REALIZADO] },
      ...naOrdemDaLinha(amb, [
        { label: "Revisão", iso: amb?.[F.REVISAO] },
        { label: "Aprovação do executivo", iso: amb?.[F.APROVACAO_DO_EXECUTIVO] },
      ], MARCOS_DO_EXECUTIVO),
      { label: "MDF/Bordo na fabrica", iso: amb?.[F.MDF_BORDO] },
      { label: "Marcenaria termino", iso: amb?.[F.MARCENARIA_TERMINO] },
      { label: "Limpeza/Embalagem", iso: amb?.[F.LIMPEZA_EMBALAGEM] },
      { label: "Carregamento", iso: amb?.[F.CARREGAMENTO] },
    ];
    return (
      <Block>
        <SectionTitle>Marcos</SectionTitle>
        {marcos.map((m) => (
          <FieldRow key={m.label}>
            <FieldLabel>{m.label}</FieldLabel>
            <FieldValue style={{ color: m.iso ? undefined : "rgba(0,0,0,0.4)" }}>
              {m.iso ? fmtData(m.iso) : "—"}
            </FieldValue>
          </FieldRow>
        ))}
      </Block>
    );
  }

  // Backend
  const p = item.raw?.pedido;
  if (!p) return null;
  const marcos = [
    { label: "Pedido recebido", iso: p.created_at },
    { label: "Iniciou produção", iso: p.iniciado_producao_em },
    { label: "Iniciou expedição", iso: p.iniciado_expedicao_em },
    { label: "Entregue", iso: p.entregue_em },
  ];
  return (
    <Block>
      <SectionTitle>Marcos</SectionTitle>
      {marcos.map((m) => (
        <FieldRow key={m.label}>
          <FieldLabel>{m.label}</FieldLabel>
          <FieldValue style={{ color: m.iso ? undefined : "rgba(0,0,0,0.4)" }}>
            {m.iso ? fmtData(m.iso) : "—"}
          </FieldValue>
        </FieldRow>
      ))}
    </Block>
  );
}

function DrillPanel({ item, onNavigate }) {
  const linkFichaCompleta = item.origem === "backend" && item.raw?.pedido?.id
    ? `/pedidos/${item.raw.pedido.id}`
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Resumo */}
      <Block>
        <SectionTitle>Resumo</SectionTitle>
        <FieldRow>
          <FieldLabel>Origem</FieldLabel>
          <FieldValue>
            <OriginBadge>{item.origem === "backend" ? "Backend (ERP)" : "Planilha"}</OriginBadge>
          </FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Número</FieldLabel>
          <FieldValue>{item.numeroProjeto}</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Cliente</FieldLabel>
          <FieldValue>{item.cliente}</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Ambiente</FieldLabel>
          <FieldValue>{item.ambiente}</FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Etapa atual</FieldLabel>
          <FieldValue>
            <Pill $tone={ETAPA_TONE[item.etapa]}>{ETAPA_LABEL[item.etapa]}</Pill>
          </FieldValue>
        </FieldRow>
        <FieldRow>
          <FieldLabel>Tempo na etapa</FieldLabel>
          <FieldValue>{fmtDias(item.diasNaEtapa)}</FieldValue>
        </FieldRow>
        {item.atrasoDias !== null && (
          <FieldRow>
            <FieldLabel>Prazo</FieldLabel>
            <FieldValue>
              <Pill $tone={atrasoTone(item.atrasoDias)}>{fmtAtraso(item.atrasoDias)}</Pill>
            </FieldValue>
          </FieldRow>
        )}
        <FieldRow>
          <FieldLabel>Prioridade</FieldLabel>
          <FieldValue>
            <Pill $tone={PRIORIDADE_TONE[item.prioridade] || "neutral"}>{item.prioridade}</Pill>
          </FieldValue>
        </FieldRow>
      </Block>

      {/* Timeline horizontal */}
      <Block>
        <SectionTitle>Jornada do pedido</SectionTitle>
        <TimelineHorizontal etapaAtual={item.etapa} />
        <InlineHint>
          Etapas em sequencia. Atual destacada. Marcos com data abaixo.
        </InlineHint>
      </Block>

      {/* Datas-chave */}
      <DatasMarcos item={item} />

      {/* Atalhos */}
      {linkFichaCompleta && (
        <Block>
          <SectionTitle>Atalhos</SectionTitle>
          <PrimaryButton type="button" onClick={() => onNavigate(linkFichaCompleta)}>
            <FiExternalLink size={14} aria-hidden="true" /> Ver ficha do pedido
          </PrimaryButton>
        </Block>
      )}

      {item.origem === "planilha" && (
        <InlineHint>
          Este item vem da planilha do Gerenciador. Para edicao, use a tela
          operacional da etapa correspondente.
        </InlineHint>
      )}
    </div>
  );
}

export default DrillPanel;
